import calculateCache from './calculateCache';
import get from 'lodash/get';

export const updateLabels = (labels, labelData) => {
  if (typeof labels !== 'object' || typeof labelData !== 'object') {
    throw new Error('Invalid parameters provided');
  }

  const { id, ...rest } = labelData;
  const updatedLabelData = { labelId: id?.toString(), ...rest };
  const { masterSubjectId } = updatedLabelData;

  // Check if labels[masterSubjectId] exists, otherwise use an empty array as a fallback
  const subjectLabels = labels[masterSubjectId]?.labels || [];

  // Get the current subjectLabels and add the new data to it
  const currentSubjectLabels = [
    ...Object.values(subjectLabels),
    updatedLabelData,
  ];

  // Replace the object with the same labelId in the subjectLabels array
  const updatedSubjectLabels = currentSubjectLabels.reduce(
    (accumulator, label) => {
      const existingLabelIndex = accumulator.findIndex(
        (item) => item.labelId === label.labelId,
      );

      if (existingLabelIndex > -1) {
        accumulator[existingLabelIndex] = label;
      } else {
        accumulator.push(label);
      }

      return accumulator;
    },
    [],
  );

  return {
    ...labels,
    [masterSubjectId]: calculateCache({
      subjectLabels: updatedSubjectLabels,
    }),
  };
};

export const addLabels = (labels, labelData) => {
  if (typeof labels !== 'object' || typeof labelData !== 'object') {
    throw new Error('Invalid parameters provided');
  }

  const { id, ...rest } = labelData;
  const updatedLabelData = { labelId: id, ...rest };
  const { masterSubjectId } = updatedLabelData;

  // Get the current subjectLabels and add the new data to it
  const currentSubjectLabels = labels[masterSubjectId]?.labels
    ? [...Object.values(labels[masterSubjectId].labels), updatedLabelData]
    : [updatedLabelData];

  // Replace the object with the same labelId in the subjectLabels array
  return {
    ...labels,
    [masterSubjectId]: calculateCache({
      subjectLabels: currentSubjectLabels,
    }),
  };
};

export const removeLabels = (labels, labelData) => {
  if (typeof labels !== 'object' || typeof labelData !== 'object') {
    throw new Error('Invalid parameters provided');
  }

  const { id, ...rest } = labelData;
  const updatedLabelData = { labelId: id, ...rest };
  const { masterSubjectId, labelId } = updatedLabelData;

  if (!masterSubjectId) {
    throw new Error('Invalid label data provided');
  }

  const currentSubjectLabels = labels[masterSubjectId]?.labels
    ? [...Object.values(labels[masterSubjectId].labels)]
    : [];

  // Remove the parent label and its children
  const updatedLabelsArray = currentSubjectLabels.filter((label) => {
    if (parseInt(label.labelId, 10) === parseInt(labelId, 10)) {
      // Remove the parent label with null parentId
      return false;
    }
    if (parseInt(label.parentId, 10) === parseInt(labelId, 10)) {
      // Remove the child labels of the parent label
      return false;
    }
    return true;
  });

  // Update the labels object with the new labels
  return {
    ...labels,
    [masterSubjectId]: calculateCache({
      subjectLabels: updatedLabelsArray,
    }),
  };
};

export const reorderLabels = (labels, labelData, masterSubjectId) => {
  if (typeof labels !== 'object' || typeof labelData !== 'object') {
    throw new Error('Invalid parameters provided');
  }

  // Check if labels[masterSubjectId] exists, otherwise use an empty array as a fallback
  const subjectLabels = labels[masterSubjectId]?.labels || {};

  // Replace order in the labels where label ID is a match in labelData
  const updatedSubjectLabels = Object.values(subjectLabels).reduce(
    (accumulator, label) => {
      const order = get(labelData, label.labelId, label.order);
      accumulator.push({
        ...label,
        order,
      });
      return accumulator;
    },
    [],
  );

  return {
    ...labels,
    [masterSubjectId]: calculateCache({
      subjectLabels: updatedSubjectLabels,
    }),
  };
};
